<template>
  <div class="panel">
    <div class="panel-title">Mise à jour du statut des sociétés</div>

    <div class="panel-grid">
      <hit-select
        class="min-w-[32rem] w-full"
        :label="'Société'"
        :options="companyOptions"
        :value="selectedSchemaId"
        item-id-property="id"
        :label-fields="['label']"
        clearable
        @change="onCompanyChange"
      />

      <hit-select
        class="min-w-[20rem] w-full"
        :label="'Nouveau statut'"
        :options="statusOptions"
        :value="selectedStatus"
        item-id-property="id"
        :label-fields="['label']"
        clearable="false"
        @change="onStatusChange"
      />

      <div v-if="selectedSchemaId" class="status-cell">
        <div v-if="currentStatusLoading" class="status-pill">
          Statut actuel: chargement…
        </div>
        <div v-else-if="currentStatus" class="status-pill">
          Statut actuel:
          <span class="status-value">{{ currentStatus }}</span>
        </div>
        <div v-else class="status-pill status-pill-warn">
          Statut actuel: indisponible
        </div>
      </div>
    </div>

    <div class="mt-3">
      <label class="block text-sm font-medium text-slate-700 mb-1">
        Message personnalisé (optionnel)
      </label>
      <textarea
        v-model="customMessage"
        class="hit-textarea"
        rows="4"
        placeholder="Ajoutez un message personnalisé qui sera inclus dans l’email."
      />
    </div>

    <div class="mt-4 flex gap-2">
      <hit-button
        :label="'Exécuter'"
        color="accent"
        prefix-icon="play_arrow"
        :disabled="running || !canRun"
        @click="run"
      />
      <hit-button
        :label="'Réinitialiser'"
        color="button"
        prefix-icon="reset_changes"
        :disabled="running"
        @click="resetForm"
      />
    </div>

    <div v-if="result" class="result mt-4">
      <div class="result-title" :class="{ok: result.ok, ko: !result.ok}">
        {{ result.ok ? 'Succès' : 'Erreur' }}
      </div>
      <pre class="result-pre">{{ resultText }}</pre>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';

export default defineComponent({
  name: 'ScriptUpdateCompanyStatus',
  components: {HitButton, HitSelect},
  data() {
    return {
      companyOptions: [],
      selectedSchemaId: null,
      selectedStatus: 'NEW',
      customMessage: '',
      running: false,
      result: null,
      currentStatusLoading: false,
      currentStatus: null,
    };
  },
  computed: {
    statusOptions() {
      const values = ['NEW', 'PENDING_APPROVAL', 'VALIDATED'];
      return values.map((v) => ({id: v, label: v}));
    },
    canRun() {
      return !!this.selectedSchemaId && !!this.selectedStatus;
    },
    resultText() {
      try {
        return JSON.stringify(this.result, null, 2);
      } catch {
        return String(this.result);
      }
    },
  },
  async mounted() {
    await this.loadCompanyOptions();
  },
  methods: {
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({limit: 500});
        const rows = res?.rows || [];
        this.companyOptions = rows.map((r) => {
          const label = [r.designation, r.vat_no].filter(Boolean).join(' – ');
          return {id: r.schema_id || r.id, label: label || r.id};
        });
      } catch {
        this.companyOptions = [];
      }
    },
    onCompanyChange(value) {
      this.selectedSchemaId = value;
      this.result = null;
      this.fetchCurrentStatus();
    },
    onStatusChange(value) {
      this.selectedStatus = value;
      this.result = null;
    },
    resetForm() {
      this.selectedSchemaId = null;
      this.selectedStatus = 'NEW';
      this.customMessage = '';
      this.currentStatus = null;
      this.currentStatusLoading = false;
    },
    async fetchCurrentStatus() {
      const schemaId = this.selectedSchemaId;
      if (!schemaId) {
        this.currentStatus = null;
        this.currentStatusLoading = false;
        return;
      }
      this.currentStatusLoading = true;
      this.currentStatus = null;
      try {
        const res = await adminApi.getCompanyVerificationStatus(schemaId);
        const status = res?.details?.verification_status || null;
        if (this.selectedSchemaId === schemaId) {
          this.currentStatus = status;
        }
      } catch {
        if (this.selectedSchemaId === schemaId) {
          this.currentStatus = null;
        }
      } finally {
        if (this.selectedSchemaId === schemaId) {
          this.currentStatusLoading = false;
        }
      }
    },
    async run() {
      if (!this.canRun) return;
      this.running = true;
      this.result = null;
      try {
        const res = await adminApi.runScript(
          '/scripts/update_company_status/run',
          {
            schema_id: this.selectedSchemaId,
            new_status: this.selectedStatus,
            custom_message: (this.customMessage || '').trim() || null,
          }
        );
        this.result = res;
        await this.fetchCurrentStatus();
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.running = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.panel {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.82);
  padding: 0.9rem;
}

.panel-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
  margin-bottom: 0.75rem;
}

.panel-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.status-cell {
  margin-left: auto;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  border: 1px solid rgba(16, 185, 129, 0.9);
  background: color-mix(in srgb, var(--color-accent, #059669) 82%, #022c22);
  color: rgba(255, 255, 255, 0.96);
  box-shadow: 0 4px 10px rgba(5, 150, 105, 0.3);
  font-size: 0.82rem;
}

.status-value {
  font-weight: 800;
  letter-spacing: 0.02em;
}

.status-pill-warn {
  color: rgba(180, 83, 9, 1);
  border-color: rgba(249, 115, 22, 0.25);
  background: rgba(255, 247, 237, 1);
  box-shadow: 0 1px 3px rgba(249, 115, 22, 0.25);
}

.hit-textarea {
  width: 100%;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 0.6rem;
  padding: 0.6rem 0.75rem;
  background: #fff;
  color: rgba(15, 23, 42, 0.9);
}

.result-title {
  font-weight: 700;
}
.result-title.ok {
  color: #03695a;
}
.result-title.ko {
  color: #b91c1c;
}
.result-pre {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.15rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.6rem 0.65rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
